import type { RegistryApp, RoleMapRow } from '@/api/console';
import type { OrgAssignment, OrgLevel } from '@/api/orgAdmin';
import type {
    RoleMapGroup,
    RoleMapScope,
    RoleMapSortDirection,
    RoleMapSortField,
    RoleMapStatusFilter,
} from './types';

/**
 * EAA-1837: pure helpers behind the "Liên kết vai trò" table. Dependency-free
 * (no queries, no store reads) so the grouping and filter/sort maths stay easy
 * to reason about and cheap to unit test.
 *
 * `accountCountsByRoleKey` and `ACCOUNT_COUNT_MAX_PAGES` are carried over
 * verbatim from the EAA-1817 coverage board — the table's "Tài khoản" column
 * needs exactly the same numbers the chip editor did. The coverage-board-only
 * helpers (`coverageCell`, `appDotTone`, `buildAppBoard`, `computeScopeDiff`)
 * were removed with that screen: they projected chips into a per-app board and
 * have no meaning for a flat table row.
 */

/** EAA-1817, carried over: rows for one (app, role_key) pair in the DEFAULT
 *  (ecosystem-wide) scope only. */
export function defaultRowsFor(rows: RoleMapRow[], appCode: string, roleKey: string): RoleMapRow[] {
    return rows.filter(
        (r) => r.application === appCode && r.role_key === roleKey && r.organization_id === null
    );
}

/**
 * EAA-1837: staleness of ONE row. Prefers the server's unified `is_stale`
 * (added by this ticket) and falls back to the pre-existing two-signal OR so
 * the table still reads correctly against an older API build — the FE must
 * never treat "field missing" as "healthy".
 */
export function isRowStale(row: RoleMapRow): boolean {
    if (typeof row.is_stale === 'boolean') return row.is_stale;
    return row.app_role === null || !row.app_role_active;
}

/** EAA-1837: rows visible in a scope. `default` = ecosystem-wide rows; `org` =
 *  only the acting org's own overrides (which is exactly the slice every bulk
 *  endpoint operates on — see `api/console.ts`). Soft-deleted rows are dropped:
 *  bulk-delete flips `is_active`, so they must leave the table. */
export function rowsInScope(
    rows: RoleMapRow[],
    scope: RoleMapScope,
    actingOrgId: string | null
): RoleMapRow[] {
    return rows.filter((row) => {
        if (!row.is_active) return false;
        if (scope === 'default') return row.organization_id === null;
        return actingOrgId !== null && row.organization_id === actingOrgId;
    });
}

/**
 * EAA-1837: collapse RoleMap rows into ONE group per `(role_key, application)`.
 *
 * Every id in the pair lands in `group.ids` — see `RoleMapGroup`'s docstring
 * for why accumulating all of them (rather than keeping a single "primary" row)
 * is a correctness requirement and not a stylistic choice.
 */
export function groupRoleMapRows(
    rows: RoleMapRow[],
    apps: RegistryApp[],
    accountCounts: Map<string, number>
): RoleMapGroup[] {
    const appNameByCode = new Map(apps.map((app) => [app.code, app.name]));
    const groups = new Map<string, RoleMapGroup>();

    for (const row of rows) {
        const key = `${row.application}::${row.role_key}`;
        const stale = isRowStale(row);
        let group = groups.get(key);
        if (!group) {
            group = {
                key,
                role_key: row.role_key,
                application: row.application,
                appName: appNameByCode.get(row.application) ?? row.application,
                ids: [],
                appRoles: [],
                organization_id: row.organization_id,
                isStale: false,
                accountCount: accountCounts.get(row.role_key) ?? 0,
            };
            groups.set(key, group);
        }
        group.ids.push(row.id);
        group.appRoles.push({
            id: row.id,
            app_role: row.app_role ?? '—',
            app_role_name: row.app_role_name ?? row.app_role ?? '—',
            stale,
        });
        // A group reads "Tạm dừng" as soon as ANY of its chips is dead — the
        // design's `librarian`/`library_admin` row. Mirrors the server export's
        // own `group["is_stale"] or is_stale(row)` accumulation.
        group.isStale = group.isStale || stale;
    }

    return Array.from(groups.values());
}

export interface RoleMapFilterState {
    search: string;
    status: RoleMapStatusFilter[];
    apps: string[];
}

export function hasActiveFilters(filters: RoleMapFilterState): boolean {
    return (
        filters.search.trim().length > 0 || filters.status.length > 0 || filters.apps.length > 0
    );
}

/**
 * EAA-1837: client-side filtering. `GET /api/v1/role-maps` takes no query
 * params at all (`RoleMapListCreateView.get` reads none), so there is no server
 * search/status/app filter to delegate to — and the endpoint already returns
 * the full list in one response, which is what the previous screen relied on too.
 *
 * The search box matches `role_key` AND `app_role`: the design puts a magnifier
 * in BOTH the "Role chuẩn" and the "Vai trò trong ứng dụng" header, and both
 * point at this one input, so it has to cover both identifiers for the second
 * icon to mean anything. Placeholder copy stays verbatim ("Tìm role chuẩn…").
 */
export function filterGroups(groups: RoleMapGroup[], filters: RoleMapFilterState): RoleMapGroup[] {
    const query = filters.search.trim().toLowerCase();
    const appSet = new Set(filters.apps);
    const statusSet = new Set(filters.status);

    return groups.filter((group) => {
        if (appSet.size > 0 && !appSet.has(group.application)) return false;
        if (statusSet.size > 0 && !statusSet.has(group.isStale ? 'stale' : 'normal')) return false;
        if (!query) return true;
        if (group.role_key.toLowerCase().includes(query)) return true;
        // EAA-1817: match the NAME as well as the key. The chips print names
        // now, so a search that only looked at keys would fail to find the very
        // text on screen.
        return group.appRoles.some(
            (chip) =>
                chip.app_role.toLowerCase().includes(query) ||
                chip.app_role_name.toLowerCase().includes(query)
        );
    });
}

export function sortGroups(
    groups: RoleMapGroup[],
    field: RoleMapSortField,
    direction: RoleMapSortDirection
): RoleMapGroup[] {
    const dir = direction === 'asc' ? 1 : -1;
    const sorted = [...groups];
    sorted.sort((a, b) => {
        if (field === 'accounts') {
            // Ties fall back to role_key so the order is stable across renders
            // (many rows share an account count of 0).
            const delta = a.accountCount - b.accountCount;
            if (delta !== 0) return delta * dir;
            return a.role_key.localeCompare(b.role_key, 'vi');
        }
        if (field === 'application') {
            const delta = a.appName.localeCompare(b.appName, 'vi');
            if (delta !== 0) return delta * dir;
            return a.role_key.localeCompare(b.role_key, 'vi');
        }
        return a.role_key.localeCompare(b.role_key, 'vi') * dir;
    });
    return sorted;
}

/**
 * EAA-1817: account counts per role_key, from the acting org's Assignments
 * (Level → role_key). Real data, not invented — but capped: the assignments
 * endpoint paginates and there is no "count by level" aggregate, so
 * `useAccountCounts` walks up to MAX_PAGES of page_size 100 (10,000 accounts)
 * and stops. For an org past that size the counts under-report rather than hang
 * the page.
 *
 * NOTE (EAA-1837): the server DOES now have a per-role_key aggregate —
 * `RoleMapService.account_counts_for_role_keys` — but it is only reachable
 * through the CSV export, not exposed as its own endpoint, so this walk stays
 * the FE's only source for the on-screen "Tài khoản" column.
 */
export const ACCOUNT_COUNT_MAX_PAGES = 100;

/** One org override that differs from the ecosystem default for a pair. */
export interface ScopeOverrideSummary {
    role_key: string;
    application: string;
    appName: string;
    added: string[];
    removed: string[];
}

/**
 * EAA-1817 (frame 59): client-side +/- diff for every `(role_key, application)`
 * pair that has org-scoped rows. Mirrors `RoleMapService.scope_diff` using the
 * already-loaded `GET /role-maps` payload — there is no whole-list diff endpoint.
 */
export function computeScopeOverrideSummaries(
    rows: RoleMapRow[],
    actingOrgId: string | null,
    apps: RegistryApp[]
): ScopeOverrideSummary[] {
    if (!actingOrgId) return [];

    const appNameByCode = new Map(apps.map((app) => [app.code, app.name]));

    function activeRoleKeys(appCode: string, roleKey: string, orgId: string | null): string[] {
        const keys = new Set<string>();
        for (const row of rows) {
            if (!row.is_active) continue;
            if (row.application !== appCode || row.role_key !== roleKey) continue;
            if (row.organization_id !== orgId) continue;
            if (row.app_role) keys.add(row.app_role);
        }
        return Array.from(keys).sort((a, b) => a.localeCompare(b, 'vi'));
    }

    const pairKeys = new Set<string>();
    for (const row of rows) {
        if (!row.is_active) continue;
        if (row.organization_id !== actingOrgId) continue;
        pairKeys.add(`${row.application}::${row.role_key}`);
    }

    const summaries: ScopeOverrideSummary[] = [];
    for (const pairKey of pairKeys) {
        const [application, role_key] = pairKey.split('::');
        const defaultKeys = activeRoleKeys(application, role_key, null);
        const overrideKeys = activeRoleKeys(application, role_key, actingOrgId);
        const added = overrideKeys.filter((key) => !defaultKeys.includes(key));
        const removed = defaultKeys.filter((key) => !overrideKeys.includes(key));
        if (added.length === 0 && removed.length === 0 && overrideKeys.length === 0) continue;
        summaries.push({
            role_key,
            application,
            appName: appNameByCode.get(application) ?? application,
            added,
            removed,
        });
    }

    return summaries.sort((a, b) => {
        const appDelta = a.appName.localeCompare(b.appName, 'vi');
        if (appDelta !== 0) return appDelta;
        return a.role_key.localeCompare(b.role_key, 'vi');
    });
}

export function accountCountsByRoleKey(
    assignments: OrgAssignment[],
    levels: OrgLevel[]
): Map<string, number> {
    const roleKeyByLevelId = new Map<string, string>();
    for (const level of levels) roleKeyByLevelId.set(level.id, level.role_key);

    const counts = new Map<string, number>();
    for (const a of assignments) {
        if (!a.is_active || !a.level_id) continue;
        const roleKey = a.role_key ?? roleKeyByLevelId.get(a.level_id);
        if (!roleKey) continue;
        counts.set(roleKey, (counts.get(roleKey) ?? 0) + 1);
    }
    return counts;
}
